import 'core-js/stable';
import 'regenerator-runtime/runtime';
import '@zillow/constellation-fonts/legacy-zillow-fonts.css';
import '@zillow/constellation-fonts/zillow-fonts.css';

import { hydrate, QueryClient, QueryClientProvider } from '@zg-rentals/react-query5';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { setClient as setPaymentClient } from '@zg-rentals/ts-rent-payment-client/client';
import { setClient as setRentalManagerClient } from '@zg-rentals/ts-rental-manager-api-client/client';
import { BASE_PATHS } from '@common/constants/AppConstants';

import { Router, useLocation } from 'react-router-dom';
import { bootstrap } from './bootstrap';
import { createRoot } from 'react-dom/client';
import { connect, Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import cx from 'classnames';
import ThemeProvider from '@zg-rentals/multibrand-ui/lib/ThemeProvider';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { ThemeConstellation } from '@zillow/constellation';
import { createAuthProvider } from '@zg-rentals/particles-js-auth-hooks';
import AppRoutes from '@common/AppRoutes';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import configureStore from '@common/utils/configureStore';
import DebugContainer from './components/DebugContainer';
import { getPathWithPersistedQueryParams, parseQueryString } from '@zg-rentals/string-utils';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { logError } from '@zg-rentals/log-error';
import { useSiftScience } from '@zg-rentals/sift-science';
import { useComscorePageTracking } from '@zg-rentals/comscore/client';
import { trackComscoreEvent } from './analytics';

/* Dev goes through this app's own proxies (same origin, cookies + CSRF header attach cleanly) instead of
   calling QA cross-origin, whose edge rejects preflights from localhost. */
setPaymentClient(getClient().extend({ prefixUrl: '/rental' }));
setRentalManagerClient(
  getClient().extend({
    prefixUrl: __DEV__ ? `${BASE_PATHS.RENTAL_MANAGER}/proxy/` : '/',
  }),
);

bootstrap.register();

declare const window: any;

// Add fetch polyfill for browsers that don't support it
if (typeof fetch === 'undefined') {
  require('whatwg-fetch');
}

const initialState = window.__INITIAL_STATE__;
const { store, history } = configureStore(initialState, createBrowserHistory());

window.store = store;
const browserHistory = history;
window.browserHistory = history;

browserHistory.listen((location: any, action?: string) => {
  const navAction = action ?? (location as any)?.action;
  if (navAction === 'PUSH' || navAction === 'REPLACE') {
    window.scrollTo(0, 0);
  }
});

window.browserHistory.block((location, action) => {
  const { pathname: targetPath, search: targetSearchQueryParams, state = {} } = location;

  if (action === 'PUSH' && state.persistQueryParams !== true) {
    const currentSearchQueryParams = window.document.location.search;

    // add target search to whitelist
    const queryParamObj = parseQueryString(targetSearchQueryParams);
    const tempWhiteList = Object.keys(queryParamObj);

    // rebuild full path
    const fullPath = `${targetPath}${currentSearchQueryParams}${targetSearchQueryParams}`;

    // set path with persisted query params
    const targetNavPath = getPathWithPersistedQueryParams(fullPath, tempWhiteList);

    // timeout is required otherwise query param is not displayed in url
    setTimeout(() => {
      const currentState = { ...window.history.state, persistQueryParams: true };
      window.history.replaceState(currentState, document.title, targetNavPath);
    }, 0);
  }

  return true;
});

window.router = {
  redirectTo(t) {
    return browserHistory.replace(t);
  },
  transitionTo(t) {
    const currentState = { ...window.history.state, scrollTop: document.body.scrollTop };
    window.history.replaceState(currentState, document.title, window.location.href);
    return browserHistory.push(t);
  },
};

const getBrandClassName = (brand) =>
  cx({
    hpx: brand === 'hotpads',
    zsg: brand === 'zillow',
    txl: brand === 'trulia',
  });

const user = store.getState().user;

const AuthProvider = createAuthProvider({
  initialUser: user,
  initialSessionExpirationDate: user.sessionExpirationDate,
});

const Analytics = () => {
  const location = useLocation();
  useComscorePageTracking(() => trackComscoreEvent({}), location.pathname);
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30 * 1000,
    },
  },
});

if (window.__QUERY_CLIENT__) {
  hydrate(queryClient, window.__QUERY_CLIENT__);
}

const ClientApp = connect(({ app }: any) => ({ brand: app.brand }))((props: any) => {
  useSiftScience({ email: user?.email, isImpersonator: Boolean(user?.impersonatorUserToken) });

  const innerTree = (
    <ThemeProvider brand={props.brand}>
      <SCThemeProvider theme={ThemeConstellation}>
        <Router history={history}>
          <Analytics />
          <div className={getBrandClassName(props.brand)}>
            {__DEV__ && <DebugContainer />}
            <AppRoutes
              isLandlordAuthReactEnabled={getBooleanConfig('isLandlordAuthReactEnabled', false)}
              isRenterAuthReactEnabled={getBooleanConfig('isRenterAuthReactEnabled', false)}
            />
          </div>
        </Router>
      </SCThemeProvider>
    </ThemeProvider>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>{innerTree}</AuthProvider>
    </QueryClientProvider>
  );
});

try {
  createRoot(document.getElementById('app-root')!).render(
    <RpErrorBoundary>
      <Provider store={store}>
        <ClientApp />
      </Provider>
    </RpErrorBoundary>,
  );
} catch (err) {
  logError({ error: err });
}
